// src/utils/gmc/merchantApi.js

import axios from 'axios';
import { auth } from '../../firebase/init';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safemcc-portal-gateway-v2-bvn24355.ew.gateway.dev'

// Helper for getting Firebase auth token
const getAuthTokenForMerchant = async () => {
    try {
        if (!auth.currentUser) {
            await new Promise(resolve => setTimeout(resolve, 1500)); 
            if (!auth.currentUser) {
                console.error('[merchantApi.js] getAuthTokenForMerchant: No authenticated user after delay.');
                throw new Error('No authenticated user for Merchant API');
            }
        }
        const token = await auth.currentUser.getIdToken(true);
        return token;
    } catch (error) {
        console.error('[merchantApi.js] Failed to get auth token for Merchant');
        throw error;
    }
};

const createAxiosConfig = async (options = {}) => {
    let token;
    try {
        token = await getAuthTokenForMerchant();
        return {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    } catch (error) {
        console.error('[merchantApi.js] Failed to create Axios config for Merchant');
        throw error;
    }
};


/**
 * Fetches the list of GMC accounts from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC accounts.
 * @returns {Promise<Array>} Promise resolving to an array of GMC account objects.
 */
export const fetchAccountsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-accounts';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC accounts`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of users with access from the BigQuery table via the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the users with access.
 * @returns {Promise<Array>} Promise resolving to an array of user access objects.
 */
export const fetchUsersFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-users';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC users`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Fetches the list of GMC pending requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC pending requests.
 * @returns {Promise<Array>} Promise resolving to an array of GMC pending request objects.
 */
export const fetchPendingRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-pending-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC pending requests`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GMC approved requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC approved requests.
 * @returns {Promise<Array>} Promise resolving to an array of GMC approved request objects.
 */
export const fetchApprovedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-approved-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC approved requests`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GMC rejected requests from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC rejected requests.
 * @returns {Promise<Array>} Promise resolving to an array of GMC rejected request objects.
 */
export const fetchRejectedRequestsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-rejected-requests';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC rejected requests`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Fetches the list of GMC audit logs from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the audit logs.
 * @returns {Promise<Array>} Promise resolving to an array of GMC audit log objects.
 */
export const fetchAuditLogsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-audit-logs';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC audit logs`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Fetches the list of GMC sub-account audit logs from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC sub-account audit logs.
 * @returns {Promise<Array>} Promise resolving to an array of GMC sub-account audit log objects.
 */
export const fetchSubAccountAuditLogsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-sub-account-audit-logs';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC sub-account audit logs`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Fetches the list of GMC sub-accounts from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC sub-accounts.
 * @returns {Promise<Array>} Promise resolving to an array of GMC sub-account objects.
 */
export const fetchSubAccountsFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-sub-accounts';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC sub-accounts`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Fetches the list of GMC sub-account users from the backend API.
 * @param {boolean} forceRefresh - Whether to force a refresh of the GMC sub-account users.
 * @returns {Promise<Array>} Promise resolving to an array of GMC sub-account user objects.
 */
export const fetchSubAccountUsersFromApi = async (forceRefresh = false) => {
    const endpointPath = '/api/gmc/gmc-sub-account-users';
    const fullUrl = `${API_BASE_URL}${endpointPath}?_t=${new Date().getTime()}${forceRefresh ? '&force=true' : ''}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.get(fullUrl, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error fetching GMC sub-account users`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Creates an access binding for a user in a GMC account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRoles (array),
 *      and requestDetails
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const createAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gmc/grant-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error creating access binding`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Removes an access binding for a user in a GMC account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestedBy, 
 *      requestedFor, 
 *      previousRoles (array)
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const removeAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gmc/remove-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error removing access binding`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};



/**
 * Modifies an access binding for a user in a GMC account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      previousRoles (array), 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRoles (array)
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const modifyAccessBindingFromApi = async (requestData) => {
    const endpointPath = '/api/gmc/modify-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error modifying access binding`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Approves a user's pending access request to a GMC account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRoles (array), 
 *      requestedOn, 
 *      approvedBy
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const approvePendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/gmc/approve-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error approving pending request`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Rejects a user's pending access request to a GMC account.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      accountId, 
 *      accountName, 
 *      requestId, 
 *      requestedBy, 
 *      requestedFor, 
 *      requestedRoles (array), 
 *      requestedOn, 
 *      rejectedBy
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const rejectPendingRequestFromApi = async (requestData) => {
    const endpointPath = '/api/gmc/reject-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error rejecting pending request`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};


/**
 * Creates a batch of access bindings for user(s) to GMC account(s). Uses same endpoint as createAccessBindingFromApi.
 * @param {Object} requestData - Request data containing: 
 *      operationType, 
 *      requestedBy,
 *      requestDetails,
 *      batchRequests
 * @returns {Promise<Object>} Promise resolving to the response object.
 */
export const createBatchAccessBindingsFromApi = async (requestData) => {
    const endpointPath = '/api/gmc/grant-access-rights';
    const fullUrl = `${API_BASE_URL}${endpointPath}`;
    try {
        const config = await createAxiosConfig();
        const response = await axios.post(fullUrl, requestData, config);
        return response.data;
    } catch (error) {
        console.error(`[merchantApi.js] Error creating batch access bindings`);
        console.error(`[merchantApi.js] Status: ${error.response?.status}`);
        console.error(`[merchantApi.js] Code: ${error.code}`);
        console.error(`[merchantApi.js] Message: ${error.message}`);
        console.error(`[merchantApi.js] Data: ${error.response?.data ? JSON.stringify(error.response.data) : 'N/A'}`);
        throw error;
    }
};
